import _ from 'underscore';
import { getUtcTime, getTimestamp } from '@bingads-webui-universal/primitive-utilities';
import { constants } from '../constants';
import { Logger } from './logger';
import {
  IScenario, IActivity, KeyOfFunc, IPerfLogger,
  TStartLogArgs,
  TStopLogArgs,
  TMonitorLogArgs,
  TMonitorAsyncLogArgs,
  TPerfLogArgs,
} from '../types';

/**
 * Default Perf Logger
 */
export class PerfLogger extends Logger implements IPerfLogger {
  /**
   * start - used to measure start of an activity
   */
  start(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [
      api,
    ]: TStartLogArgs,
  ): void {
    super.log({
      api,
      isMethodEnter: true,
      timeStamp: getUtcTime(),
      logCategory: constants.LOGCATEGORY.PERFORMANCE,
      logLevel: constants.LOGLEVEL.INFO,
      entryType: constants.ENTRYTYPE.ACTIVITY,
      activity,
      scenario,
      parentScenario,
    });
  }

  /**
   * stop - used to measure stop of an activity
   */
  stop(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [
      result,
      api,
    ]: TStopLogArgs,
  ): void {
    super.log({
      api,
      isMethodEnter: false,
      timeStamp: getUtcTime(),
      pass: _.isUndefined(result) ? true : result,
      logCategory: constants.LOGCATEGORY.PERFORMANCE,
      logLevel: constants.LOGLEVEL.INFO,
      entryType: constants.ENTRYTYPE.ACTIVITY,
      activity,
      scenario,
      parentScenario,
    });
  }

  /**
   * monitor - can be used to monitor and measure the duration of a function
   */
  monitor<T, K extends KeyOfFunc<T>>(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [
      func,
      context,
    ]: TMonitorLogArgs<T, K>,
  ): void {
    const originalFunc = context[func] as Function;

    if (!_.isFunction(originalFunc)) {
      return;
    }

    context[func] = ((...args: any[]) => {
      const t0 = getTimestamp();

      const result = originalFunc.apply(context, args);

      const timeTaken = getTimestamp() - t0;

      super.log({
        api: originalFunc.name,
        timeTaken,
        timeStamp: getUtcTime(),
        logCategory: constants.LOGCATEGORY.PERFORMANCE,
        logLevel: constants.LOGLEVEL.INFO,
        entryType: constants.ENTRYTYPE.METHOD,
        activity,
        scenario,
        parentScenario,
      });

      return result;
    }) as unknown as T[K];
  }

  /**
   * monitorAsync - can be used to measure the duration of a promise
   */
  monitorAsync<T>(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [
      promise,
    ]: TMonitorAsyncLogArgs<T>,
  ): Promise<T> {
    const t0 = getTimestamp();

    // log monitor start
    super.log({
      isMethodEnter: true,
      timeStamp: getUtcTime(),
      logCategory: constants.LOGCATEGORY.PERFORMANCE,
      logLevel: constants.LOGLEVEL.INFO,
      entryType: constants.ENTRYTYPE.METHOD,
      activity,
      scenario,
      parentScenario,
    });

    const onPromiseDone = () => {
      const timeTaken = getTimestamp() - t0;

      // log monitor stop
      super.log({
        isMethodEnter: false,
        timeTaken,
        timeStamp: getUtcTime(),
        logCategory: constants.LOGCATEGORY.PERFORMANCE,
        logLevel: constants.LOGLEVEL.INFO,
        entryType: constants.ENTRYTYPE.METHOD,
        activity,
        scenario,
        parentScenario,
      });
    };

    promise.then(onPromiseDone, onPromiseDone);
    return promise;
  }

  /**
   * perf - logs passed in args as perf logs
   */
  perf(
    scenario: IScenario,
    activity: IActivity,
    parentScenario: IScenario,
    [
      message,
      api,
    ]: TPerfLogArgs,
  ): void {
    super.log({
      api,
      message,
      timeStamp: getUtcTime(),
      logCategory: constants.LOGCATEGORY.PERFORMANCE,
      logLevel: constants.LOGLEVEL.INFO,
      entryType: constants.ENTRYTYPE.MESSAGE,
      activity,
      scenario,
      parentScenario,
    });
  }

  getLogMethods() {
    return ['start', 'stop', 'monitor', 'monitorAsync', 'perf'];
  }
}
